import { useId, useState } from 'react';
import { Alert, Avatar, Box, Button, Radio, Typography } from '@mui/material';
import { HugeiconsIcon } from '@hugeicons/react';
import { Calendar03IconStroke } from '@/components/icons';
import { C, T } from '@/util/Localize';
import { anyFeeRange, buildTeamCards, formatAnyFeeRange } from './team-rules';
import type { TeamCartOptions, TeamMember, TeamSelection } from './team-types';

/** Legacy's own words (`cart-team.asp` :123-161). */
export const ANY_AVAILABLE_LABEL = 'Any Available';
export const OUT_OF_AREA_LABEL = 'Outside of serviceable area';
export const SHOW_ALL_LABEL = 'Show All Photographers';
/**
 * Legacy has no such state: with nobody to offer, its page died on a ReferenceError
 * (`cart-location.js` :514-516 calling `cartOrder.cannotOrder` where cart-order.js is not loaded).
 */
export const NO_PHOTOGRAPHER_MESSAGE =
    'No photographer is available for this appointment. Please call to schedule.';

export interface TeamPickerProps extends TeamCartOptions {
    /**
     * The photographers who may be offered, in the order the host got them. The picker sorts them,
     * applies the display limit and decides whether "Any Available" stays (`buildTeamCards`); it
     * does NOT decide who is eligible — a host passes only members it may show this caller.
     */
    members: readonly TeamMember[];
    /** Controlled: a member's id, `'any'`, or `null` for nothing chosen. */
    value: TeamSelection;
    onChange: (value: TeamSelection) => void;
    /** The host offers the "Any Available" card (it still hides when one photographer is left). */
    anyAvailable?: boolean;
    /** Cards before "Show All Photographers"; "Any Available" counts as one. Default 12. */
    displayLimit?: number;
    /**
     * Whether every photographer is shown. Controlled when given — a host that needs to know which
     * cards are on screen holds this and calls `buildTeamCards` with the same inputs; otherwise the
     * picker keeps it itself.
     */
    isShowingAll?: boolean;
    onShowAll?: () => void;
    /** Show the "+$x Travel Fee" line. The host decides whether fees apply at all. */
    showTravelFees?: boolean;
    /** Money in the brand's currency. Defaults to the app's locale and currency, two decimals. */
    formatMoney?: (amount: number) => string;
    /**
     * A photo URL for a member. The module does not know how a host stores or sizes media, so the
     * host builds the URL; no URL means initials.
     */
    photoUrl?: (member: TeamMember) => string | null | undefined;
    disabled?: boolean;
    /** Accessible name of the radio group. */
    label?: string;
    /** Shown when `members` is empty. */
    emptyMessage?: string;
}

const PHOTO_SIZE = 88;

function initials(name: string | null | undefined): string {
    return (name ?? '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase() ?? '')
        .join('');
}

interface TeamCardProps {
    name: string;
    inputId: string;
    groupName: string;
    isSelected: boolean;
    disabled: boolean;
    isOutOfArea?: boolean;
    feeText?: string | null;
    photo: React.ReactNode;
    testId: string;
    onSelect: () => void;
}

/** One card: the whole thing is the radio's label, as legacy's `<label class="member">` is. */
function TeamCard({
    name,
    inputId,
    groupName,
    isSelected,
    disabled,
    isOutOfArea = false,
    feeText,
    photo,
    testId,
    onSelect
}: TeamCardProps) {
    return (
        <Box
            component="label"
            htmlFor={inputId}
            data-testid={testId}
            data-selected={isSelected ? 'true' : 'false'}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                minWidth: 0,
                border: 2,
                borderRadius: 1,
                borderStyle: isOutOfArea ? 'dashed' : 'solid',
                borderColor: isSelected ? 'primary.main' : isOutOfArea ? 'warning.main' : 'divider',
                bgcolor: isSelected ? 'action.selected' : 'background.paper',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                '&:has(input:focus-visible)': {
                    outline: 2,
                    outlineStyle: 'solid',
                    outlineColor: 'primary.main',
                    outlineOffset: 2
                }
            }}
        >
            {photo}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
                <Radio
                    id={inputId}
                    name={groupName}
                    size="small"
                    checked={isSelected}
                    disabled={disabled}
                    onChange={onSelect}
                    sx={{ p: 0.5, mr: 0.5, flexShrink: 0 }}
                />
                <Box sx={{ minWidth: 0, pt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                        {name}
                    </Typography>
                    {feeText && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                        >
                            {feeText}
                        </Typography>
                    )}
                    {isOutOfArea && (
                        <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: 'block', fontWeight: 700 }}
                        >
                            {T(OUT_OF_AREA_LABEL)}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

/**
 * The photographer cards — the React counterpart of legacy `View/inc/cart-team.asp` +
 * `View/js/cart-team.js` in single-member mode: one radio card per photographer, an optional
 * "Any Available" card, the 12-card limit with "Show All Photographers", the "+$x Travel Fee" line
 * and, for members a host flags `inArea: false` (staff only), "Outside of serviceable area".
 *
 * Controlled and host-agnostic: data in through props, the choice out through `onChange`. It never
 * fetches, never reads a session and never decides who is eligible. The radios are native inputs
 * sharing one `name`, so arrow keys move between cards and Tab enters / leaves the group, as any
 * radio group does. Colours come from the MUI theme only, so a design-derived theme restyles it.
 *
 * `productFilter`, `waiveTravelFee` and `anyServiceArea` (`TeamCartOptions`) are accepted and NOT
 * YET USED — they are the cart's inputs; see `team-types.ts`.
 */
export function TeamPicker({
    members,
    value,
    onChange,
    anyAvailable = false,
    displayLimit,
    isShowingAll,
    onShowAll,
    showTravelFees = false,
    formatMoney = amount => C(amount, 2),
    photoUrl,
    disabled = false,
    label,
    emptyMessage
}: TeamPickerProps) {
    const groupName = useId();
    const [isShowingAllOwn, setIsShowingAllOwn] = useState(false);
    const isAllShown = isShowingAll ?? isShowingAllOwn;

    const cards = buildTeamCards(members, { anyAvailable, displayLimit, isShowingAll: isAllShown });

    if (cards.all.length === 0) {
        return (
            <Alert severity="info" data-testid="team-picker-empty">
                {emptyMessage ?? T(NO_PHOTOGRAPHER_MESSAGE)}
            </Alert>
        );
    }

    const travelFeeText = (amount: string | null) =>
        amount ? `+${amount} ${T('Travel Fee')}` : null;
    const anyFeeText = showTravelFees
        ? travelFeeText(formatAnyFeeRange(anyFeeRange(cards.all), formatMoney, T))
        : null;

    const handleShowAll = () => {
        setIsShowingAllOwn(true);
        onShowAll?.();
    };

    return (
        <Box data-testid="team-picker">
            <Box
                role="radiogroup"
                aria-label={label ?? T('Photographer')}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: 1.5
                }}
            >
                {cards.hasAnyCard && (
                    <TeamCard
                        name={T(ANY_AVAILABLE_LABEL)}
                        inputId={`${groupName}-any`}
                        groupName={groupName}
                        isSelected={value === 'any'}
                        disabled={disabled}
                        feeText={anyFeeText}
                        testId="team-card-any"
                        onSelect={() => onChange('any')}
                        photo={
                            <Avatar
                                variant="rounded"
                                aria-hidden="true"
                                sx={{
                                    width: PHOTO_SIZE,
                                    height: PHOTO_SIZE,
                                    bgcolor: 'action.hover',
                                    color: 'text.secondary'
                                }}
                            >
                                <HugeiconsIcon icon={Calendar03IconStroke} size={40} />
                            </Avatar>
                        }
                    />
                )}
                {cards.members.map(member => {
                    const name = member.name ?? T('Photographer');
                    const fee =
                        showTravelFees && typeof member.travelFee === 'number' && member.travelFee
                            ? travelFeeText(formatMoney(member.travelFee))
                            : null;
                    return (
                        <TeamCard
                            key={member.id}
                            name={name}
                            inputId={`${groupName}-${member.id}`}
                            groupName={groupName}
                            isSelected={value === member.id}
                            disabled={disabled}
                            isOutOfArea={member.inArea === false}
                            feeText={fee}
                            testId={`team-card-${member.id}`}
                            onSelect={() => onChange(member.id)}
                            photo={
                                <Avatar
                                    variant="rounded"
                                    src={photoUrl?.(member) ?? undefined}
                                    // Decorative: the name is right below, in the same label, and
                                    // the initials must not become part of the radio's name.
                                    alt=""
                                    aria-hidden="true"
                                    sx={{
                                        width: PHOTO_SIZE,
                                        height: PHOTO_SIZE,
                                        bgcolor: 'action.hover',
                                        color: 'text.secondary'
                                    }}
                                >
                                    {initials(member.name)}
                                </Avatar>
                            }
                        />
                    );
                })}
            </Box>
            {cards.hasMore && (
                <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                    <Button variant="text" onClick={handleShowAll} disabled={disabled}>
                        {T(SHOW_ALL_LABEL)}
                    </Button>
                </Box>
            )}
        </Box>
    );
}
